import { useMemo } from 'react'
import { useEventTypes } from '@/hooks/use-event-types'
import { rankItem, rankings } from '@tanstack/match-sorter-utils'
import { useContacts } from '@/stores/contacts.store'
import { useLeadsList } from '@/stores/leads.store'
import { useEventsList } from '@/stores/events.store'
import { useInvoicesList } from '@/stores/invoices.store'
import { formatFullName } from '@/lib/utils'
import { formatTimestamp } from '@/lib/date-utils'
import type { Contact } from '@/gen/cortado/v1/contacts_pb'
import type { InquiryLite, EventLite } from '@/gen/cortado/v1/inquiries_pb'
import type { InvoiceLite } from '@/gen/cortado/v1/invoices_pb'

export type SearchResultType = 'contact' | 'lead' | 'event' | 'invoice'

export interface SearchResult {
  id: string
  type: SearchResultType
  title: string
  subtitle: string
  href: string
  metadata?: string
}

interface UseGlobalSearchOptions {
  query: string
  enabled?: boolean
  /** Gates the underlying data fetches (contacts/leads/events/invoices). Default true. */
  fetchEnabled?: boolean
  maxResultsPerCategory?: number
}

interface UseGlobalSearchResult {
  results: {
    contacts: SearchResult[]
    leads: SearchResult[]
    events: SearchResult[]
    invoices: SearchResult[]
  }
  totalCount: number
  isLoading: boolean
  hasResults: boolean
}

/**
 * Formats cents to currency string
 */
function formatCurrency(cents: bigint | number | undefined): string {
  if (cents === undefined) return ''
  const dollars = Number(cents) / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(dollars)
}

/**
 * Ranks an item against a search query using multiple search keys
 * Returns the best rank among all keys
 */
function getBestRank(
  searchKeys: string[],
  query: string
): number {
  let bestRank: number = rankings.NO_MATCH

  for (const key of searchKeys) {
    if (!key) continue
    const result = rankItem(key, query, { threshold: rankings.CONTAINS })
    if (result.rank > bestRank) {
      bestRank = result.rank
    }
  }

  return bestRank
}

/**
 * Hook for global search across multiple entity types
 *
 * Uses match-sorter-utils for fuzzy matching with the following search fields:
 * - Contacts: firstName, lastName, email, phoneNumber
 * - Leads (Inquiries): contact name, event type
 * - Events: contact name, event date
 * - Invoices: invoice number, contact name, title
 */
export function useGlobalSearch({
  query,
  enabled = true,
  fetchEnabled = true,
  maxResultsPerCategory = 5,
}: UseGlobalSearchOptions): UseGlobalSearchResult {
  // Fetch data from existing stores
  const { contacts, isLoading: contactsLoading } = useContacts(undefined, { enabled: fetchEnabled })
  const { inquiries, isLoading: leadsLoading } = useLeadsList({ enabled: fetchEnabled })
  const { events, isLoading: eventsLoading } = useEventsList({ enabled: fetchEnabled })
  const { invoices, isLoading: invoicesLoading } = useInvoicesList({ enabled: fetchEnabled })
  const { getEventTypeLabel } = useEventTypes()

  // NOTE: a disabled query reports isLoading: false (isPending: true, isFetching:
  // false), so this naturally stays false while fetchEnabled is false — no
  // permanent skeleton risk here.
  const isLoading = contactsLoading || leadsLoading || eventsLoading || invoicesLoading

  // Memoize search results
  const results = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase()

    // Return empty results if query is too short or disabled
    if (!enabled || trimmedQuery.length < 2) {
      return {
        contacts: [],
        leads: [],
        events: [],
        invoices: [],
      }
    }

    // Search contacts
    const contactResults = contacts
      .map((contact: Contact) => {
        const searchKeys = [
          contact.firstName,
          contact.lastName,
          formatFullName(contact.firstName, contact.lastName),
          contact.email || '',
          contact.phoneNumber || '',
        ]
        const rank = getBestRank(searchKeys, trimmedQuery)
        return { contact, rank }
      })
      .filter(({ rank }) => rank > rankings.NO_MATCH)
      .sort((a, b) => b.rank - a.rank)
      .slice(0, maxResultsPerCategory)
      .map(({ contact }): SearchResult => ({
        id: contact.id,
        type: 'contact',
        title: formatFullName(contact.firstName, contact.lastName),
        subtitle: contact.email || contact.phoneNumber || '',
        href: `/contacts/${contact.id}`,
        metadata: contact.phoneNumber && contact.email ? contact.phoneNumber : undefined,
      }))

    // Search leads (inquiries)
    const leadResults = inquiries
      .map((inquiry: InquiryLite) => {
        const searchKeys = [
          inquiry.contactFirstName,
          inquiry.contactLastName,
          formatFullName(inquiry.contactFirstName, inquiry.contactLastName),
          getEventTypeLabel(inquiry.eventTypeKey).toLowerCase(),
        ]
        const rank = getBestRank(searchKeys, trimmedQuery)
        return { inquiry, rank }
      })
      .filter(({ rank }) => rank > rankings.NO_MATCH)
      .sort((a, b) => b.rank - a.rank)
      .slice(0, maxResultsPerCategory)
      .map(({ inquiry }): SearchResult => ({
        id: inquiry.id,
        type: 'lead',
        title: formatFullName(inquiry.contactFirstName, inquiry.contactLastName),
        subtitle: getEventTypeLabel(inquiry.eventTypeKey) || 'Lead',
        href: `/leads/${inquiry.id}`,
        metadata: inquiry.firstEventDate ? formatTimestamp(inquiry.firstEventDate) : undefined,
      }))

    // Search events
    const eventResults = events
      .map((event: EventLite) => {
        const searchKeys = [
          event.contactFirstName,
          event.contactLastName,
          formatFullName(event.contactFirstName, event.contactLastName),
          event.eventDate ? formatTimestamp(event.eventDate) : '',
        ]
        const rank = getBestRank(searchKeys, trimmedQuery)
        return { event, rank }
      })
      .filter(({ rank }) => rank > rankings.NO_MATCH)
      .sort((a, b) => b.rank - a.rank)
      .slice(0, maxResultsPerCategory)
      .map(({ event }): SearchResult => ({
        id: event.id,
        type: 'event',
        title: formatFullName(event.contactFirstName, event.contactLastName),
        subtitle: event.eventDate ? formatTimestamp(event.eventDate) : 'Unscheduled',
        href: `/events/${event.bookingId}`,
        metadata: event.eventGuests ? `${event.eventGuests} guests` : undefined,
      }))

    // Search invoices
    const invoiceResults = invoices
      .map((invoice: InvoiceLite) => {
        const searchKeys = [
          `INV-${invoice.invoiceNumber}`,
          invoice.contactName || '',
          invoice.title || '',
        ]
        const rank = getBestRank(searchKeys, trimmedQuery)
        return { invoice, rank }
      })
      .filter(({ rank }) => rank > rankings.NO_MATCH)
      .sort((a, b) => b.rank - a.rank)
      .slice(0, maxResultsPerCategory)
      .map(({ invoice }): SearchResult => ({
        id: invoice.id,
        type: 'invoice',
        title: invoice.title || `Invoice #${invoice.invoiceNumber}`,
        subtitle: invoice.contactName || '',
        href: `/invoices/${invoice.id}`,
        metadata: formatCurrency(invoice.totalCents),
      }))

    return {
      contacts: contactResults,
      leads: leadResults,
      events: eventResults,
      invoices: invoiceResults,
    }
  }, [query, enabled, contacts, inquiries, events, invoices, maxResultsPerCategory, getEventTypeLabel])

  const totalCount =
    results.contacts.length +
    results.leads.length +
    results.events.length +
    results.invoices.length

  return {
    results,
    totalCount,
    isLoading,
    hasResults: totalCount > 0,
  }
}
